import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, CheckCircle2, Loader2, MessageSquare } from "lucide-react";
import Layout from "@/components/layout/Layout";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { GreenButton, GhostButton } from "@/components/Buttons";
import { money } from "@/lib/signs";

export default function Proof() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showNote, setShowNote] = useState(false);
    const [note, setNote] = useState("");

    useEffect(() => {
        api.get(`/proof/${token}`).then(({ data }) => setData(data)).catch(() => { }).finally(() => setLoading(false));
    }, [token]);

    const payMore = Number(data?.balance_due || 0) > 0 && data?.pay_link;   // 11.2 rule 6: a job change the shop priced
    const approve = async () => {
        setBusy(true);
        try {
            await api.post(`/proof/${token}/approve`);
            setData((p) => ({ ...p, proof_status: "approved" }));
            if (payMore) { window.location.assign(data.pay_link); return; }
            toast.success("Approved — we're printing it!");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const requestChanges = async () => {
        setBusy(true);
        try {
            await api.post(`/proof/${token}/changes`, { message: note });
            setData((p) => ({ ...p, proof_status: "changes_requested", proof_note: note }));
            setShowNote(false);
            toast.success("Got it — we'll reach out about the changes");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (loading) return <Layout hideTicker><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;
    if (!data) {
        return (
            <Layout hideTicker>
                <div className="mx-auto max-w-2xl px-4 py-24 text-center">
                    <h1 className="font-display text-3xl font-black">Proof not found</h1>
                    <p className="mt-2 text-zinc-400">This link may have expired. Text us at (347) 573-3875 and we'll resend it.</p>
                </div>
            </Layout>
        );
    }

    const approved = data.proof_status === "approved";
    const changes = data.proof_status === "changes_requested";

    return (
        <Layout hideTicker>
            <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6" data-testid="proof-page">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-green">Proof · Order {data.order_number}</p>
                <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">
                    {approved ? "Approved — we're on it." : changes ? "Changes requested." : "Here's your proof."}
                </h1>
                <p className="mt-3 text-base text-zinc-300">
                    {approved ? "Your job is queued for print. We'll email you the moment it's ready for pickup."
                        : changes ? "Thanks — we got your notes and we'll follow up before printing."
                            : "This is exactly how your job is set up. Approve it and we'll print it."}
                </p>

                <div className="mt-8 space-y-5">
                    {data.items.map((it, i) => (
                        <div key={i} className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03]" data-testid="proof-item">
                            {it.proof_url && (
                                <img src={mediaUrl(it.proof_url)} alt={it.product_name} className="w-full bg-black/60 object-contain" data-testid="proof-image" />
                            )}
                            <div className="flex flex-wrap items-end justify-between gap-3 p-5">
                                <div>
                                    <h3 className="font-display text-lg font-bold">{it.product_name}</h3>
                                    <p className="mt-1 text-sm text-zinc-400">{it.summary}</p>
                                </div>
                                <p className="font-mono text-lg font-bold text-brand-green">Qty {it.quantity} · {money(it.line_total)}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-zinc-400">Order total</span>
                        <span className="font-display text-2xl font-black text-brand-green">{money(data.total)}</span>
                    </div>

                    {payMore && !approved && (
                        <p className="mt-3 text-sm text-zinc-300" data-testid="proof-pay-more-note">{data.balance_for || "Job change"}: your choices or note changed the job. Approving opens the {money(data.balance_due)} payment.</p>
                    )}
                    {approved ? (
                        <div className="mt-5 flex items-center gap-3 rounded-2xl border border-brand-green/30 bg-brand-green/[0.08] p-4" data-testid="proof-approved">
                            <BadgeCheck className="text-brand-green" />
                            <p className="text-sm text-zinc-200">Proof approved. Nothing else to do.</p>
                        </div>
                    ) : (
                        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                            <GreenButton onClick={approve} disabled={busy} className="flex-1" data-testid="proof-approve">
                                {busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} {payMore ? `Approve and pay ${money(data.balance_due)} more` : "Approve & print"}
                            </GreenButton>
                            <GhostButton onClick={() => setShowNote(!showNote)} className="flex-1" data-testid="proof-request-changes">
                                <MessageSquare size={16} /> Request changes
                            </GhostButton>
                        </div>
                    )}

                    {showNote && !approved && (
                        <div className="mt-4">
                            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                                placeholder="Tell us what to change — size, placement, colors…"
                                className="w-full rounded-2xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white outline-none focus:border-brand-green"
                                data-testid="proof-note-input" />
                            <GreenButton onClick={requestChanges} disabled={busy || !note.trim()} className="mt-3" data-testid="proof-send-changes">Send changes</GreenButton>
                        </div>
                    )}

                    {changes && data.proof_note && (
                        <p className="mt-4 rounded-2xl border border-white/10 bg-black/30 p-4 text-sm text-zinc-300" data-testid="proof-note">"{data.proof_note}"</p>
                    )}
                </div>
            </div>
        </Layout>
    );
}
